import React, { useCallback, useContext, useEffect, useState } from "react";
import WeekDay from "./WeekDay";
import Events from "../EventsList/Events";
import CalendarHeader from "../CalendarHeader";
import LoadingIndicator from "../LoadingIndicator";
import classnames from "classnames";
import { CT } from "../../../../consts";
import PoweredBy from "../PoweredBy";
import SearchInput from "../AiSearch/SearchInput";
import useAiSearch from "../../../../hooks/useAiSearch";
import useWeekPeriod from "../../../../hooks/useWeekPeriod";
import WidgetSettingsContext from "../../../../contexts/WidgetSettingsContext";
import { isSameDay } from "../../../../helpers/date";
import DateRangeSelector from "./DateRangeSelector/DateRangeSelector";

/**
 * @param {Object} calendar
 * @param {Object} events
 * @param {function} onSelectedDateChange
 * @param {function} onUpcomingNavigationChange
 * @param {Object} selectedDate
 * @param {boolean} initialized
 * @param {function} onLoadMore
 * @param {string} previewType - one of PT values
 * @param {function} onChangeFilter
 * @param {function} onKeywordSearch
 * @param {Object} selectedFilters
 * @param {boolean} embedded
 */
const WeeklyView = (props) => {
  const { calendar, events } = props;

  const { periodStart, days, isRolling } = useWeekPeriod();

  const [state, setState] = useState({
    selectedDay: props.selectedDate,
    currentWeek: new Date(props.selectedDate),
  });

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      selectedDay: props.selectedDate,
      currentWeek: new Date(props.selectedDate),
    }));
  }, [props.selectedDate]);

  const { hasAIEnabled } = useAiSearch(calendar.model.integration);
  const widgetSettings = useContext(WidgetSettingsContext);

  /**
   *
   * @param day
   */
  const onSelectDay = useCallback((day) => {
    setState((prev) => ({ ...prev, selectedDay: new Date(day) }));
  }, []);

  /**
   *
   * @param week
   */
  const onChangeWeek = useCallback(
    (week) => {
      props.onSelectedDateChange(new Date(week));
    },
    [props.onSelectedDateChange]
  );

  const eventsNumberOption = widgetSettings["eventsNumberOption"];
  const eventsNumberCount = widgetSettings["eventsNumber"];

  const eventsLocation = widgetSettings["wwEventsLocation"];
  const allowChangingWeeks =
    Number.parseInt(widgetSettings["wwAllowChangingWeeks"]) === 1;
  const showEventIndication =
    Number.parseInt(widgetSettings["wwShowEventsIndication"]) === 1;
  const enforceMobile =
    Number.parseInt(widgetSettings["wwEnforceMobile"]) === 1;
  const useEventAccent =
    Number.parseInt(widgetSettings["generalUseEventAccent"]) === 1;
  const recurringShowBadge =
    Number.parseInt(widgetSettings["recurringShowBadge"]) === 1;
  const featuredShowBadge =
    Number.parseInt(widgetSettings["featuredShowBadge"]) === 1;
  const showEventLocation =
    Number.parseInt(widgetSettings["wwShowEventLocation"]) === 1;

  const watermark =
    calendar.model && calendar.model.is_watermarked ? <PoweredBy /> : null;

  useEffect(() => {
    if (eventsNumberOption == "date_range") {
      if (eventsNumberCount && eventsNumberCount.indexOf(":") > -1) {
        const [start, end] = eventsNumberCount.split(":");
        const startDate = new Date(start);
        if (!isSameDay(startDate, state.currentWeek)) {
          props.onSelectedDateChange(new Date(startDate));
        }
      }
    }
  }, [eventsNumberCount]);

  const calendarID = "dce_calendar__" + calendar.model.uuid;

  // First day of the visible period: calendar week start, or the rolling
  // period start when "Start from today" is enabled.
  const firstDay = periodStart(state.currentWeek);

  const calendarDays = [...Array(days).keys()].map((i, idx) => {
    const weekDay = new Date(firstDay);
    weekDay.setDate(firstDay.getDate() + i);

    return (
      <WeekDay
        key={calendarID + "-wdk-" + idx}
        calendar={calendar}
        events={events}
        day={weekDay}
        isSelected={state.selectedDay && isSameDay(state.selectedDay, weekDay)}
        onSelectDay={onSelectDay}
        showEventDetails={eventsLocation != "events_list"}
        eventsLocation={eventsLocation}
        showEventPresenseIndication={showEventIndication}
        useEventAccent={useEventAccent}
        showRecurring={recurringShowBadge}
        showFeatured={featuredShowBadge}
        showLocation={showEventLocation}
        embedded={props.embedded}
      />
    );
  });

  const loading =
    calendar.rsvp || calendar.fetch || calendar.delete || calendar.update;

  const eventsList =
    eventsLocation == "events_list" && !events.fetch ? (
      <Events
        events={
          state.selectedDay
            ? events.collection.filter((e) => {
                return state.selectedDay.isBetween(
                  new Date(e.start_time * 1000),
                  new Date(e.end_time * 1000),
                  "day"
                );
              })
            : events.collection
        }
        calendarSlug={calendar.model.slug}
        embedded={props.embedded}
        loading={loading}
        error={props.calendar.rsvpError}
        onLoadMore={props.onLoadMore}
        hasMore={events.collection.length != events.totalRows}
        calendarID={calendarID}
      />
    ) : null;

  const eventsListContent =
    events.fetch || !props.initialized ? <LoadingIndicator /> : eventsList;

  const search =
    hasAIEnabled && widgetSettings["aiEnabled"] ? (
      <SearchInput
        searchEvents={props.searchEvents}
        fetchEvents={props.fetchEvents}
        className={"weekly-calendar--search"}
      />
    ) : null;

  return (
    <div
      className={classnames(
        `weekly-calendar dce--${CT.weekly} weekly-calendar--days-${days}`,
        {
          pill: eventsLocation == "pills",
          stack: eventsLocation == "stack",
          rolling: isRolling,
        }
      )}
      style={{ "--ww-days": days }}
    >
      <div className={"weekly-calendar--heading-content"}>
        <CalendarHeader
          disabled={events.fetch}
          calendar={calendar}
          currentDate={props.selectedDate}
          onChangeDate={null}
          events={events.collection}
          locations={events.locations}
          onUpcomingNavigationChange={props.onUpcomingNavigationChange}
          showMonthSelector={false}
          selectedFilters={props.selectedFilters}
          onChangeFilter={props.onChangeFilter}
          onSearch={props.onKeywordSearch}
        />

        {search}
        <DateRangeSelector
          currentWeek={firstDay}
          onChangeWeek={onChangeWeek}
          allowChangingWeeks={allowChangingWeeks}
          showUpcomingEventsOnly={eventsNumberOption == "upcoming"}
        />
      </div>
      <div className={"weekly-calendar--container"}>{calendarDays}</div>
      {eventsListContent}
      {watermark}
    </div>
  );
};

export default WeeklyView;
